/**
 * api.ts · Phase 4 REST client
 * Thin fetch wrappers around the backend contract. Base URL is read from
 * `VITE_API_BASE_URL` at build time (with a `localhost:8000` default so
 * `npm run dev` works out of the box on any dev machine).
 *
 * Every request supports AbortController via the `signal` option so
 * React unmount cancels are proper. Errors are wrapped in `ApiError`
 * carrying the HTTP status + parsed body so hooks can transition their
 * state machines deterministically.
 */
// ------------------------------------------------------------------
// Config + primitives
// ------------------------------------------------------------------
/** Fallback base URL — dev servers on 5173 talk to backends on 8000. */
const DEFAULT_BASE_URL = "http://localhost:8000";
/** Read the API base URL from Vite env, with a safe fallback. */
export function getBaseUrl() {
    // Vite injects `import.meta.env` at build time.
    // Guard the property access to survive tests that stub globals.
    const env = import.meta.env;
    const value = env?.VITE_API_BASE_URL;
    if (typeof value === "string" && value.length > 0) {
        return value.replace(/\/+$/, "");
    }
    return DEFAULT_BASE_URL;
}
/** Build a WebSocket URL for the same origin as the REST base. */
export function getWebSocketUrl(path = "/ws/vehicles") {
    const base = getBaseUrl();
    const httpToWs = base
        .replace(/^http:/i, "ws:")
        .replace(/^https:/i, "wss:");
    return `${httpToWs}${path.startsWith("/") ? path : `/${path}`}`;
}
/** Structured HTTP failure so callers can pattern-match cleanly. */
export class ApiError extends Error {
    status;
    body;
    url;
    constructor(message, url, status, body) {
        super(message);
        this.name = "ApiError";
        this.url = url;
        this.status = status;
        this.body = body;
    }
    /** Terse operator-facing message (e.g., "HTTP 500 · fleet gateway rejected"). */
    get short() {
        return `HTTP ${this.status}`;
    }
}
/** Low-level JSON GET. */
async function getJson(path, { signal, headers } = {}) {
    const url = `${getBaseUrl()}${path}`;
    const res = await fetch(url, {
        method: "GET",
        signal,
        headers: { Accept: "application/json", ...(headers ?? {}) },
    });
    if (!res.ok) {
        const body = await safeParse(res);
        throw new ApiError(`GET ${path} failed with HTTP ${res.status}`, url, res.status, body);
    }
    return (await res.json());
}
/** Low-level JSON POST. */
async function postJson(path, body, { signal, headers } = {}) {
    const url = `${getBaseUrl()}${path}`;
    const res = await fetch(url, {
        method: "POST",
        signal,
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(headers ?? {}),
        },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const b = await safeParse(res);
        throw new ApiError(`POST ${path} failed with HTTP ${res.status}`, url, res.status, b);
    }
    return (await res.json());
}
async function safeParse(res) {
    const text = await res.text();
    if (!text)
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
// ------------------------------------------------------------------
// Public surface — one function per endpoint
// ------------------------------------------------------------------
/** GET /api/vehicles/live — full envelope (entities + in-band alerts). */
export function getVehiclesLive(opts) {
    return getJson("/api/vehicles/live", opts);
}
/** GET /api/alerts?state=<state> — filtered alert list. */
export function getAlerts(state, opts) {
    const q = state ? `?state=${encodeURIComponent(state)}` : "";
    return getJson(`/api/alerts${q}`, opts);
}
/** POST /api/hold — issue a hold command. Body validated server-side. */
export function postHold(req, opts) {
    return postJson("/api/hold", req, opts);
}
/** POST /api/alerts/{id}/ack — mark an alert acknowledged. */
export function ackAlert(id, opts) {
    return postJson(`/api/alerts/${encodeURIComponent(id)}/ack`, {}, opts);
}
/**
 * GET /api/sim_runs?limit=<n> — most-recent-first list of sim summaries.
 * Backend default `limit` is 50; we plumb the query param for symmetry.
 */
export function getSimRuns(limit, opts) {
    const q = typeof limit === "number" && limit > 0
        ? `?limit=${Math.round(limit)}`
        : "";
    return getJson(`/api/sim_runs${q}`, opts);
}
/** GET /api/sim_runs/{id}/trace — the full trace blob for the chart. */
export function getSimTrace(id, opts) {
    return getJson(`/api/sim_runs/${id}/trace`, opts);
}
/** Convenience: `AbortError` sniff without importing DOMException types. */
export function isAbortError(err) {
    return (err instanceof Error &&
        (err.name === "AbortError" || err.message === "The user aborted a request."));
}
